import { getConsentKey } from './cookie-consent-helpers';

declare global {
  interface Window {
    OneTrust?: {
      IsAlertBoxClosedAndValid?: () => boolean;
    };
    OnetrustActiveGroups?: string;
  }
}

/**
 * This file parses user consent for different cookie categories.
 * It does so by using the OneTrust SDK instead of directly accessing cookies.
 */
// https://my.onetrust.com/s/article/UUID-66bcaaf1-c7ca-5f32-6760-c75a1337c226?language=en_US
// Turns out sometimes OneTrust uses long category IDs and sometimes short numeric IDs.
export const CATEGORY_IDS = Object.freeze({
  performance: ['C0002', '2'],
  functional: ['C0003', '3'],
  targeting: ['C0004', '4'],
} as const);

type CategoryKeys = keyof typeof CATEGORY_IDS;

export interface ConsentService {
  updateUserConsent: (key: string, value: boolean) => void;
}

const OneTrust = {
  CATEGORY_IDS,
  ONE_TRUST_READY_EVENT_NAME: 'OneTrustReady',

  /**
   * Invokes a function as soon as OneTrust is loaded.
   * @param {function} fn - The function to be invoked
   */
  onOneTrustReady(fn: () => void) {
    if (window.OneTrust) {
      fn();
      return;
    }
    window.addEventListener(this.ONE_TRUST_READY_EVENT_NAME, fn, {
      once: true,
    });
  },

  /**
   * Stores the user's cookie preferences using the consent service.
   * @param {ConsentService} consentService - The consent service to update user consent.
   */
  storeCookiePreferences(consentService: ConsentService) {
    const userPreferences = this.getUserPreferences();
    Object.entries(userPreferences).forEach(([category, value]) => {
      const consentKey = getConsentKey(category);
      consentService.updateUserConsent(consentKey, value);
    });
  },

  /**
   * Gets the user's consent for a specific category.
   * @param {CategoryKeys} category - The category to get consent for.
   * @returns {boolean} - The user's consent for the category.
   */
  getConsentForCategory(category: CategoryKeys): boolean {
    const categoryIds = CATEGORY_IDS[category] || [];
    const activeGroups = window.OnetrustActiveGroups?.split(',') || [];
    return categoryIds.some((id) => activeGroups.includes(id));
  },

  /**
   * Gets the user's preferences for all categories.
   * @returns {Record<CategoryKeys, boolean>} - The user's preferences for all categories.
   */
  getUserPreferences(): Record<CategoryKeys, boolean> {
    const activeGroups = window.OnetrustActiveGroups?.split(',') || [];
    const userPreferences = {} as Record<CategoryKeys, boolean>;

    for (const category in CATEGORY_IDS) {
      const typedCategory = category as CategoryKeys;
      const categoryIds = CATEGORY_IDS[typedCategory];
      userPreferences[typedCategory] = categoryIds.some((id) =>
        activeGroups.includes(id)
      );
    }

    return userPreferences;
  },

  /**
   * Determines if the user has interacted with the cookie consent banner.
   * @returns {boolean} - True if the banner has been closed and consent is valid, false otherwise.
   */
  hasBannerInteraction(): boolean {
    // Use null coalescing for the case when OneTrust is not loaded.
    return window.OneTrust?.IsAlertBoxClosedAndValid?.() ?? false;
  },

  /**
   * Checks if consent data is available from OneTrust SDK.
   * @returns {boolean} - True if consent data is available, false otherwise.
   */
  hasConsentData(): boolean {
    return !!window.OnetrustActiveGroups;
  },
};

export default OneTrust;
